import * as yup from 'yup';
import { highestTcpPortNumber, smallestTcpPortNumber } from '@/core/constants';
import { emailRegex, priceListLogoRegex, russianDomainEmailRegex } from './validationRegex';
import { t } from '@/core/localization';
/** `-2,147,483,648` */
const int32NegativeLimit = -(2 ** 31);
/** `2,147,483,647` */
const int32PositiveLimit = 2 ** 31 - 1;
/** `4,294,967,295` */
const uint32Limit = 2 ** 32 - 1;
/** `9,223,372,036,854,775,808` */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Kept for completeness of the limits set.
const int64NegativeLimit = -(2 ** 63);
/** `9,223,372,036,854,775,807` */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Kept for completeness of the limits set.
const int64PositiveLimit = 2 ** 63 - 1;
/** `18,446,744,073,709,551,615` */
const uint64Limit = 2 ** 64 - 1;
// TODO: add tests for this 2 new methods
yup.addMethod(yup.number, 'int32', 
/**
 * Allows only integer values in range from `-2,147,483,648` to `2,147,483,647`.
 *
 * Also includes yup's native `integer()` check.
 */
function validateValue(message) {
    return this.test('isValidInt32', 
    // message ,// ?? t('can.be.whole.number.in.range.from.{1}.to.{2}', int32NegativeLimit, int32PositiveLimit),
    function (value) {
        const { path, createError } = this;
        // Optional case.
        if (value === undefined || value === null) {
            return true;
        }
        // yup's native `integer()` validator
        if (!Number.isInteger(value)) {
            return createError({
                path,
                message: t('the.field.must.be.a.whole.number'),
            });
        }
        if (value > int32PositiveLimit || value < int32NegativeLimit) {
            return createError({
                path,
                message: message ??
                    t('can.be.whole.number.in.range.from.{1}.to.{2}', int32NegativeLimit.toLocaleString(), int32PositiveLimit.toLocaleString()),
            });
        }
        return true;
    });
});
yup.addMethod(yup.number, 'uint32', 
/**
 * Allows only integer values in range from `0` to `4,294,967,295`.
 *
 * Also includes yup's native `integer()` check.
 */
function validateValue(message) {
    return this.test('isValidUint32', 
    //  message ?? t('can.be.whole.number.in.range.from.{1}.to.{2}', 0, uint32Limit),
    function (value) {
        const { path, createError } = this;
        // Optional case.
        if (value === undefined || value === null) {
            return true;
        }
        // yup's native `integer()` validator
        if (!Number.isInteger(value)) {
            return createError({
                path,
                message: t('the.field.must.be.a.whole.number'),
            });
        }
        if (value > uint32Limit || value < 0) {
            return createError({
                path,
                message: message ?? t('can.be.whole.number.in.range.from.{1}.to.{2}', 0, uint32Limit.toLocaleString()),
            });
        }
        return true;
    });
});
yup.addMethod(yup.number, 'id', 
/**
 * Allows only integer values in range from `1` to `uint64 max value`.
 *
 * Also includes yup's native `integer()` check.
 */
function validateValue(message) {
    return this.test('isValidUint64', 
    //  message ?? t('can.be.whole.number.in.range.from.{1}.to.{2}', 1, uint64Limit),
    function (value) {
        const { path, createError } = this;
        // // Optional case.
        // if (value === undefined || value === null) {
        // 	return true;
        // }
        // Required case.
        if (value === undefined || value === null) {
            return createError({
                path,
                message: t('field.is.required.error'),
            });
        }
        // yup's native `integer()` validator
        if (!Number.isInteger(value)) {
            return createError({
                path,
                message: t('the.field.must.be.a.whole.number'),
            });
        }
        if (value > uint64Limit || value < 1) {
            return createError({
                path,
                message: message ?? t('can.be.whole.number.in.range.from.{1}.to.{2}', 1, uint64Limit.toLocaleString()),
            });
        }
        return true;
    });
});
yup.addMethod(yup.number, 'tcpIpPortAsNumber', function validateTcpIpPort(message) {
    return this.test('isValidTcpIpPortAsNumber', message ?? t('invalid.tcp.ip.port'), function (value) {
        const { path, createError } = this;
        // Optional case.
        if (value === undefined || value === null) {
            return true;
        }
        if (value > highestTcpPortNumber || value < smallestTcpPortNumber) {
            return createError({
                path,
                message: message ?? t('invalid.tcp.ip.port'),
            });
        }
        return true;
    });
});
// TODO: get rid of? Use (following) `string().email()` or `string().russianDomainEmail()` instead.
export const strongEmail = yup.string().required().matches(emailRegex, 'Invalid email address');
yup.addMethod(yup.string, 'email', function validateEmail(message) {
    return this.matches(emailRegex, {
        message: message ?? t('invalid.email.address'),
        name: 'email',
        excludeEmptyString: true,
    });
});
yup.addMethod(yup.string, 'russianDomainEmail', function validateEmail(message) {
    return this.matches(russianDomainEmailRegex, {
        message: message ?? t('invalid.email.address'),
        name: 'russianDomainEmail',
        excludeEmptyString: true,
    });
});
// // Using custom test method
// const tcpIpPort = (message: string) => {
//   return this.test("isValidCountry", message, function (value) {
//     const { path, createError } = this;
//     if (!value) {
//       return createError({ path, message: message ?? REQUIRED_ERROR_MESSAGE });
//     }
//     if (value.length < 3) {
//       return createError({ path, message: message ?? TOO_SMALL_ERROR_MESSAGE });
//     }
//     if (!value.match(COUNTRY_NAME_REGEX)) {
//       return createError({
//         path,
//         message: message ?? INVALID_FORMAT_ERROR_MESSAGE
//       });
//     }
//     return true;
//   });
// }
yup.addMethod(yup.string, 'tcpIpPortAsString', function validateTcpIpPort(message) {
    return this.test('isValidTcpIpPortAsString', message ?? t('invalid.tcp.ip.port'), function (value) {
        const { path, createError } = this;
        // Optional case.
        if (!value) {
            return true;
        }
        if (value.length > 5) {
            return createError({ path, message: message ?? t('invalid.tcp.ip.port') });
        }
        const numericValue = Number.parseInt(value);
        if (Number.isNaN(+numericValue) || numericValue > highestTcpPortNumber || numericValue < smallestTcpPortNumber) {
            return createError({
                path,
                message: message ?? t('invalid.tcp.ip.port'),
            });
        }
        return true;
    });
});
yup.addMethod(yup.string, 'priceListLogo', function validatePriceListLogo(message) {
    return this.matches(priceListLogoRegex, {
        message: message ?? t('invalid.price.list.logo.string'),
        name: 'priceListLogo',
        excludeEmptyString: true,
    });
});
/**
 * LEGACY. Use new cbrBik fabric.
 */
export const bik = yup
    .string()
    .required()
    .matches(/^[0-9]+$/, 'Must be only digits')
    //.min(9, 'Must be exactly 9 digits')
    //.max(9, 'Must be exactly 9 digits'),
    .max(9, 'Must be maximum 9 digits');
/**
 * LEGACY. Use new fnsInn fabric.
 */
export const inn = yup
    .string()
    .required()
    .matches(/^[0-9]+$/, 'Must be only digits')
    //.min(12, 'Must be exactly 12 digits')
    //.max(12, 'Must be exactly 12 digits'),
    .max(12, 'Must be maximum 12 digits');
/**
 * LEGACY. Use new cbrPaymentAccount fabric.
 */
export const paymentAccount = yup
    .string()
    .required()
    .matches(/^[0-9]+$/, 'Must be only digits')
    //.min(20, 'Must be exactly 20 digits')
    //.max(20, 'Must be exactly 20 digits'),
    .max(20, 'Must be maximum 20 digits');
export * from 'yup';
